<template>
  <LoadingContainer :is-loading="isLoading">
    <form class="has-sticky-submit" @submit.prevent="submit">
      <div class="card">
        <div class="card-header">
          <div class="card-header-title">
            <div class="title is-6 is-marginless">
              Stand-alone Card Links
              <span class="tag">Επεξεργασία</span>
            </div>
          </div>

          <div class="card-header-icon">
            <div class="field has-addons is-marginless">
              <div class="control">
                <RouterLink
                  :to="{ name: 'content.home.standalone-cards.list' }"
                  class="button"
                >
                  <span class="icon is-small">
                    <i class="fa fa-list" />
                  </span>
                  <span>Λίστα</span>
                </RouterLink>
              </div>

              <div class="control">
                <button type="button" class="button" @click="askToDelete">
                  <span class="icon is-small">
                    <i class="fa fa-trash" />
                  </span>
                  <span>Διαγραφή</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card-content">
          <div class="columns is-multiline">
            <!-- Image (WEBP) - Same UI as Banners -->
            <div class="column is-full">
              <AddPhotos
                :title="'Εικόνα (WEBP)'"
                :preview="preview"
                :show-error="errors.has('image')"
                rule="image/webp"
                @addPhotos="handleAddPhotos"
              />
              <p class="help">Το αρχείο πρέπει να είναι WEBP.</p>
            </div>

            <!-- Title EL -->
            <div class="column is-half">
              <div class="field">
                <label class="label">Τίτλος (EL) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_el"
                    class="input"
                    type="text"
                    name="title_el"
                  />
                  <p v-show="errors.has('title_el')" class="help is-danger">
                    Εισάγετε ελληνικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <!-- Title EN -->
            <div class="column is-half">
              <div class="field">
                <label class="label">Τίτλος (EN) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_en"
                    class="input"
                    type="text"
                    name="title_en"
                  />
                  <p v-show="errors.has('title_en')" class="help is-danger">
                    Εισάγετε αγγλικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <!-- Link -->
            <div class="column is-full">
              <div class="field">
                <label class="label">Link (URL) *</label>
                <div class="control">
                  <input
                    v-validate="'required|url'"
                    v-model.trim="form.link"
                    class="input"
                    type="text"
                    name="link"
                  />
                  <p v-show="errors.has('link')" class="help is-danger">
                    Εισάγετε έγκυρο link
                  </p>
                </div>
              </div>
            </div>

            <!-- Active -->
            <div class="column is-full">
              <div class="field">
                <label class="label">
                  Εμφανές *
                  <span class="tag is-rounded ml-2">Max 5 ενεργές</span>
                </label>
                <div class="control">
                  <ToggleButton
                    :sync="true"
                    :value="form.is_active"
                    :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                    :width="30"
                    :height="15"
                    @change="updateActive"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StickyFormFooter>
        <template #left>
          <FormSubmit :is-saving="isSaving" @reset="reset" />
        </template>

        <template v-if="form.link" #right>
          <a v-tooltip="'Προβολή στο e-shop'" :href="form.link" target="_blank">
            Προβολή link στο e-shop
            <i class="fa fa-arrow-right" />
          </a>
        </template>
      </StickyFormFooter>
    </form>

    <Confirmation
      :model-name="form.title_el"
      :is-open="isDeleteOpen"
      description="Είστε βέβαιος για τη διαγραφή αυτής της κάρτας;"
      @closeModal="closeDeleteModal"
    />
  </LoadingContainer>
</template>

<script>
import request from '@/utils/request';
import StickyFormFooter from '@/views/components/StickyFormFooter';
import LoadingContainer from '@/views/components/LoadingContainer';
import FormSubmit from '@/views/components/FormSubmit';
import Confirmation from '@/views/components/Confirmation';
import AddPhotos from '@/views/components/AddPhotos';

export default {
  name: 'StandaloneCardLinksEdit',
  components: {
    LoadingContainer,
    StickyFormFooter,
    FormSubmit,
    Confirmation,
    AddPhotos,
  },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDeleteOpen: false,
      original: null,
      form: {
        id: null,
        title_el: '',
        title_en: '',
        link: '',
        is_active: false,
        image: null,
      },
      preview: '',
    };
  },

  computed: {
    // current image from API (resource provides image_url)
    imageUrl() {
      // eslint-disable-next-line dot-notation
      if (this.original && this.original['image_url']) {
        // eslint-disable-next-line dot-notation
        return this.original['image_url'];
      }
      return '';
    },
  },

  metaInfo: { title: 'Stand-alone Card Links - Edit' },

  watch: {
    '$route.params.id': {
      immediate: true,
      async handler() {
        await this.fetchItem();
      },
    },
  },

  methods: {
    async fetchItem() {
      try {
        this.isLoading = true;
        const { data } = await request.get('/home/standalone-card-links');
        const id = Number(this.$route.params.id);

        // Normalize payload (array or {data: []})
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.data)) {
          list = data.data;
        }

        const found = list.find(x => Number(x.id) === id) || null;

        if (!found) {
          this.$notify({
            type: 'error',
            title: 'Σφάλμα',
            text: 'Η κάρτα δεν βρέθηκε',
          });
          this.$router
            .push({ name: 'content.home.standalone-cards.list' })
            .catch(() => {});
          return;
        }

        this.original = found;
        this.form = {
          id: found.id,
          title_el: found.title_el || '',
          title_en: found.title_en || '',
          link: found.link || '',
          is_active: !!found.is_active,
          image: null,
        };

        this.preview = this.imageUrl || '';
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: (err && err.message) || 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    handleAddPhotos(image) {
      this.form.image = image;

      const reader = new FileReader();
      this.$validator
        .validateAll()
        .then(() => {
          reader.onload = e => {
            this.preview = e.target.result;
            this.errors.remove('image');
          };
          reader.readAsDataURL(image);
        })
        .catch(err => console.log(err));
    },

    async submit() {
      try {
        const ok = await this.$validator.validateAll();
        if (!ok) throw new Error('Client-side validation failed');

        this.isSaving = true;

        // Build ONE FormData payload (text + optional image)
        const fd = new FormData();
        fd.append('_method', 'PATCH');
        fd.append('title_el', this.form.title_el || '');
        fd.append('title_en', this.form.title_en || '');
        fd.append('link', this.form.link || '');
        fd.append('is_active', this.form.is_active ? '1' : '0');

        if (this.form.image) {
          fd.append('image', this.form.image);
        }

        const resp = await request.post(
          `/home/standalone-card-links/${this.form.id}`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        if (resp.status !== 200) {
          throw new Error(resp?.data?.message || 'To αίτημα απέτυχε');
        }

        this.isSaving = false;

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα αποθηκεύτηκε',
        });

        await this.fetchItem();
      } catch (err) {
        this.isSaving = false;
        const msg =
          err?.response?.data?.message || err?.message || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg.includes('Maximum of 5') ? 'Μέγιστο 5 ενεργές κάρτες' : msg,
        });
      }
    },

    askToDelete() {
      this.isDeleteOpen = true;
    },

    async closeDeleteModal(confirmed) {
      this.isDeleteOpen = false;
      if (!confirmed) return;

      try {
        const { data } = await request.delete(
          `/home/standalone-card-links/${this.form.id}`,
        );
        if (data && data.error) {
          throw new Error(data.message || 'Server error');
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα διαγράφηκε',
        });

        this.$router
          .push({ name: 'content.home.standalone-cards.list' })
          .catch(() => {});
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: (err && err.message) || 'To αίτημα απέτυχε',
        });
      }
    },

    reset() {
      if (!this.original) return;
      this.form = {
        id: this.original.id,
        title_el: this.original.title_el || '',
        title_en: this.original.title_en || '',
        link: this.original.link || '',
        is_active: !!this.original.is_active,
        image: null,
      };
      this.preview = this.imageUrl || '';
      this.$validator.reset();
      this.errors.remove('image');
    },
  },
};
</script>
